// Timelapse do dia (7.2): lógica pura do player, sem DOM e sem relógio. Testada em
// tests/timelapse-logica.test.ts. O player está em src/web/timelapse.ts.
//
// Quatro contas: a série por minuto (presença e trabalho) que desenha o gráfico, os picos de
// trabalho que viram marcas clicáveis, a ponte entre a posição da barra (minutos desde o
// primeiro quadro) e a hora do quadro, e o avanço do relógio simulado com a velocidade.

import type { TimelineFrame } from '../core/timeline.js';
import type { AgentStatus } from '../core/types.js';

export const MINUTO_MS = 60_000;
/** Velocidades do relógio simulado (×). A primeira é a de abrir o player. */
export const TIMELAPSE_VELOCIDADES = [60, 180, 600] as const;
export const VELOCIDADE_INICIAL = 60;
/** Dois picos ficam pelo menos esse tanto de minutos afastados (senão são o mesmo pico). */
export const PICO_DISTANCIA_MIN = 20;
export const PICOS_MAX = 8;

/** Quem trabalha de fato: executando uma ferramenta ou pensando/escrevendo a resposta. */
const TRABALHANDO: readonly AgentStatus[] = ['working', 'thinking'];

/** Um minuto do dia: quantos estão no campus e quantos trabalham nesse quadro. */
export interface PontoSerie {
  at: number;
  /** Minutos desde o primeiro quadro: é onde o ponto cai na barra e no gráfico. */
  minuto: number;
  presentes: number;
  trabalhando: number;
}

export function serieDoDia(quadros: readonly TimelineFrame[]): PontoSerie[] {
  const inicio = quadros[0]?.at ?? 0;
  return quadros.map((quadro) => ({
    at: quadro.at,
    minuto: Math.round((quadro.at - inicio) / MINUTO_MS),
    presentes: quadro.agentes.length,
    trabalhando: quadro.agentes.filter((agente) => TRABALHANDO.includes(agente.status)).length,
  }));
}

/** Um pico de trabalho do dia: o quadro (índice em quadros) que o clique mostra. */
export interface Pico {
  indice: number;
  at: number;
  minuto: number;
  trabalhando: number;
  presentes: number;
}

/**
 * Picos de trabalho: máximos locais de `trabalhando` (minuto sem ninguém trabalhando não conta).
 * Fica com os mais fortes, sem dois a menos de `distancia` minutos, e devolve em ordem de hora.
 */
export function picosDoDia(serie: readonly PontoSerie[], max = PICOS_MAX, distancia = PICO_DISTANCIA_MIN): Pico[] {
  const candidatos: number[] = [];
  serie.forEach((ponto, i) => {
    if (ponto.trabalhando <= 0) return;
    const antes = i > 0 ? serie[i - 1]!.trabalhando : -1;
    const depois = i + 1 < serie.length ? serie[i + 1]!.trabalhando : -1;
    // platô: conta só o primeiro minuto dele (o da esquerda é menor e o da direita não é maior)
    if (ponto.trabalhando > antes && ponto.trabalhando >= depois) candidatos.push(i);
  });
  candidatos.sort((a, b) => serie[b]!.trabalhando - serie[a]!.trabalhando || a - b);

  const escolhidos: number[] = [];
  for (const i of candidatos) {
    if (escolhidos.length >= max) break;
    if (escolhidos.every((j) => Math.abs(serie[j]!.minuto - serie[i]!.minuto) >= distancia)) escolhidos.push(i);
  }
  return escolhidos
    .sort((a, b) => a - b)
    .map((i) => ({ indice: i, at: serie[i]!.at, minuto: serie[i]!.minuto, trabalhando: serie[i]!.trabalhando, presentes: serie[i]!.presentes }));
}

/** Minutos inteiros entre dois instantes do dia (o comprimento da barra). */
export function duracaoEmMinutos(inicio: number, fim: number): number {
  return Math.max(0, Math.round((fim - inicio) / MINUTO_MS));
}

/** Posição da barra (minutos desde `inicio`) de um instante, presa entre 0 e o fim do dia. */
export function posicaoDoInstante(at: number, inicio: number, fim: number): number {
  return Math.min(duracaoEmMinutos(inicio, fim), Math.max(0, Math.round((at - inicio) / MINUTO_MS)));
}

/** Instante de uma posição da barra. Posição fora da barra é presa nas pontas. */
export function instanteDaPosicao(posicao: number, inicio: number, fim: number): number {
  const minutos = Math.min(duracaoEmMinutos(inicio, fim), Math.max(0, Math.round(posicao)));
  return Math.min(fim, inicio + minutos * MINUTO_MS);
}

/**
 * Índice do último quadro com instante até `em` (os quadros vêm em ordem de tempo).
 * Antes do primeiro quadro, fica no primeiro.
 */
export function indiceEm(quadros: readonly { at: number }[], em: number): number {
  let i = 0;
  while (i + 1 < quadros.length && quadros[i + 1]!.at <= em) i++;
  return i;
}

/**
 * Anda o relógio simulado: `decorrido` ms reais viram `decorrido × velocidade` no dia, sem passar
 * do fim. Tempo negativo (relógio que voltou) não anda para trás.
 */
export function avancaRelogio(relogio: number, decorrido: number, velocidade: number, fim: number): number {
  return Math.min(fim, relogio + Math.max(0, decorrido) * velocidade);
}

/** A velocidade pedida se for uma das do player; senão, a inicial. */
export function velocidadeValida(velocidade: number): number {
  return (TIMELAPSE_VELOCIDADES as readonly number[]).includes(velocidade) ? velocidade : VELOCIDADE_INICIAL;
}

/** HH:MM local, como o relógio do escritório (também é o selo 'REPLAY 14:32'). */
export function horaCurta(at: number): string {
  const d = new Date(at);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Contorno de uma série para o SVG: x é o minuto do dia, y sobe com o valor (0 em baixo, `altura`
 * em cima, em coordenadas da viewBox). Sem pontos, string vazia.
 */
export function areaDaSerie(serie: readonly PontoSerie[], valor: (p: PontoSerie) => number, altura: number, teto: number): string {
  if (!serie.length) return '';
  const escala = teto > 0 ? altura / teto : 0;
  const pontos = serie.map((p) => `${p.minuto},${(altura - valor(p) * escala).toFixed(1)}`);
  const base = altura;
  return `M${serie[0]!.minuto},${base} L${pontos.join(' L')} L${serie[serie.length - 1]!.minuto},${base} Z`;
}
